// GFM tables (the one table node). Four corrections to the markdown mapping:
//   1. `<br>` becomes `hardBreak` (without a window, `@tiptap/markdown` turns
//      HTML into text);
//   2. `|` in a cell (text, and a link's href / title) is written as `\|`
//      (`renderTableToMarkdown` does not escape it);
//   3. a cell holds paragraphs only (with `block+`, lists collapse into `<br>`
//      and do not come back);
//   4. the line before a table, `<!-- table: widths=... fix-rows=... fix-cols=... -->`,
//      is read and written as the table's column widths (em) and frozen header
//      rows / columns (`widths` / `fixRows` / `fixCols`).

import { Extension, type JSONContent } from "@tiptap/core";
import {
  renderTableToMarkdown,
  Table,
  TableCell,
  TableHeader,
} from "@tiptap/extension-table";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { m } from "@trameria/i18n";

/** `<br>` / `<br/>` / `<br />` in any case. */
const BR_RE = /^<br\s*\/?>/i;

/**
 * Inline `<br>` as `hardBreak`. marked makes HTML an `html` token, which
 * `@tiptap/markdown` turns into text on the server. Extension tokenizers run
 * before the built-in ones, so this one takes it first.
 */
export const HtmlBreak = Extension.create({
  name: "htmlBreak",
  markdownTokenName: "htmlBreak",
  markdownTokenizer: {
    name: "htmlBreak",
    level: "inline" as const,
    start(src: string) {
      const match = /<br\s*\/?>/i.exec(src);
      return match ? match.index : -1;
    },
    tokenize(src: string) {
      const match = BR_RE.exec(src);
      if (!match) return undefined;
      return { type: "htmlBreak", raw: match[0] };
    },
  },
  parseMarkdown() {
    return { type: "hardBreak" };
  },
});

/** `|` and an already escaped `\|` (so nothing is escaped twice). */
const PIPE_RE = /\\?\|/g;

function replacePipes(text: string, to: string): string {
  return text.replace(PIPE_RE, to);
}

/** `|` in cell text as `\|` (an existing `\|` is kept as one). */
export function escapeCellPipes(text: string): string {
  return replacePipes(text, "\\|");
}

/**
 * Where cell pipes wait while the table is rendered: U+FDD0, a noncharacter
 * Unicode never assigns, so it cannot come in by paste or IME. (A private use
 * character was used before; icon fonts use that range, and such a character in
 * a cell came back as `|`.) Written by code point: the character is invisible
 * and would look like an empty string.
 */
const PIPE_PLACEHOLDER = String.fromCharCode(0xfdd0);

function hidePipesInText(text: string): string {
  return replacePipes(text, PIPE_PLACEHOLDER);
}

/** Pipes in a link mark's `href` / `title` wait in the same place. */
function hidePipesInMarks(marks: JSONContent["marks"]): JSONContent["marks"] {
  if (!marks) return marks;
  return marks.map((mark) => {
    if (mark.type !== "link" || !mark.attrs) return mark;
    const attrs = { ...mark.attrs };
    if (typeof attrs.href === "string")
      attrs.href = hidePipesInText(attrs.href);
    if (typeof attrs.title === "string")
      attrs.title = hidePipesInText(attrs.title);
    return { ...mark, attrs };
  });
}

/**
 * Hides cell pipes before `renderTableToMarkdown` (which splits rows on `|`, so
 * an unescaped pipe cannot be told from a separator); the renderer turns the
 * placeholder into `\|` afterwards.
 */
function hidePipes(node: JSONContent): JSONContent {
  const mapText = (n: JSONContent): JSONContent => {
    if (n.type === "text" && typeof n.text === "string")
      return {
        ...n,
        text: hidePipesInText(n.text),
        marks: hidePipesInMarks(n.marks),
      };
    if (n.content) return { ...n, content: n.content.map(mapText) };
    return n;
  };
  return {
    ...node,
    content: (node.content ?? []).map((row) => ({
      ...row,
      content: (row.content ?? []).map((cell) => ({
        ...cell,
        content: (cell.content ?? []).map(mapText),
      })),
    })),
  };
}

/** The table option line's attributes. */
export interface TableOptionAttrs {
  widths: number[] | null;
  fixRows: 0 | 1 | 2;
  fixCols: 0 | 1 | 2;
}

/** Column width (em) used for missing widths. */
export const DEFAULT_COLUMN_EM = 8;

const OPTION_LINE_RE = /^<!--\s*table:\s*(.*?)\s*-->$/;

/**
 * Reads one option line. Unknown keys, out-of-range or repeated keys and
 * non-numbers are reported as errors (the table is kept and normalized).
 */
export function parseTableOptions(line: string): {
  attrs: TableOptionAttrs;
  errors: string[];
} {
  const attrs: TableOptionAttrs = { widths: null, fixRows: 0, fixCols: 0 };
  const errors: string[] = [];
  const match = OPTION_LINE_RE.exec(line.trim());
  if (!match) return { attrs, errors: [m.editor_table_option_shape()] };
  const seen = new Set<string>();
  for (const pair of match[1].split(/\s+/).filter(Boolean)) {
    const eq = pair.indexOf("=");
    const key = eq < 0 ? pair : pair.slice(0, eq);
    const value = eq < 0 ? "" : pair.slice(eq + 1);
    if (seen.has(key)) {
      errors.push(m.editor_table_option_twice({ key }));
      continue;
    }
    seen.add(key);
    if (key === "widths") {
      const nums = value.split(",").map((v) => Number(v));
      if (
        nums.length === 0 ||
        nums.some((n) => !Number.isFinite(n) || n <= 0)
      ) {
        errors.push(m.editor_table_option_widths());
        continue;
      }
      attrs.widths = nums.map(roundColumnEm);
    } else if (key === "fix-rows" || key === "fix-cols") {
      const n = Number(value);
      if (!(n === 0 || n === 1 || n === 2)) {
        errors.push(m.editor_table_option_fix_range({ key }));
        continue;
      }
      if (key === "fix-rows") attrs.fixRows = n;
      else attrs.fixCols = n;
    } else {
      errors.push(m.editor_table_option_unknown_key({ key }));
    }
  }
  return { attrs, errors };
}

/** The option line, or null when every value is the default. */
export function formatTableOptions(attrs: TableOptionAttrs): string | null {
  const parts: string[] = [];
  if (attrs.widths && attrs.widths.length > 0)
    parts.push(`widths=${attrs.widths.map((w) => String(w)).join(",")}`);
  if (attrs.fixRows) parts.push(`fix-rows=${attrs.fixRows}`);
  if (attrs.fixCols) parts.push(`fix-cols=${attrs.fixCols}`);
  return parts.length === 0 ? null : `<!-- table: ${parts.join(" ")} -->`;
}

/** Whether a table node writes an option line (render-blocks.ts needs it). */
export function hasTableOptions(node: JSONContent): boolean {
  return (
    formatTableOptions({
      widths: (node.attrs?.widths as number[] | null) ?? null,
      fixRows: (node.attrs?.fixRows as 0 | 1 | 2) ?? 0,
      fixCols: (node.attrs?.fixCols as 0 | 1 | 2) ?? 0,
    }) !== null
  );
}

/**
 * One column width as stored: one decimal, at least 0.1em. Without the floor a
 * rounded 0 would be written as `widths=0,8`, which the lint rejects - the
 * normalizer would write a body it refuses itself. With it, normalized output
 * always passes the lint.
 */
function roundColumnEm(n: number): number {
  return Math.max(0.1, Math.round(n * 10) / 10);
}

/**
 * Fits widths to the column count: extra ones are dropped, missing ones are
 * {@link DEFAULT_COLUMN_EM}, each rounded by {@link roundColumnEm}.
 */
export function normalizeWidths(
  widths: number[] | null,
  columns: number,
): number[] | null {
  if (!widths) return null;
  const out = widths.slice(0, columns).map(roundColumnEm);
  while (out.length < columns) out.push(DEFAULT_COLUMN_EM);
  return out;
}

/** Column count (cells of the first row). */
function columnCount(table: JSONContent): number {
  return table.content?.[0]?.content?.length ?? 0;
}

/**
 * Takes the option line only; whether a table follows is decided after
 * parsing ({@link mergeTableOptions}). Extension tokenizers run before marked's
 * `html` block tokenizer, so the comment is taken before it becomes an opaque block.
 */
export const TableOptions = Extension.create({
  name: "tableOptions",
  markdownTokenName: "tableOptions",
  markdownTokenizer: {
    name: "tableOptions",
    level: "block" as const,
    start(src: string) {
      const match = /^<!-- table:/m.exec(src);
      return match ? match.index : -1;
    },
    tokenize(src: string) {
      const match = /^<!--\s*table:.*?-->(?:\n|$)/.exec(src);
      if (!match) return undefined;
      return {
        type: "tableOptions",
        raw: match[0],
        line: match[0].replace(/\n$/, ""),
      };
    },
  },
  parseMarkdown(token) {
    return { type: "tableOptions", attrs: { line: token.line as string } };
  },
});

/**
 * After parsing (on the JSON, before `schema.nodeFromJSON`): a `tableOptions`
 * marker followed by a `table` moves its values onto the table and disappears;
 * a marker followed by anything else becomes an opaque block holding the line
 * (kept byte for byte, as a plain HTML comment).
 *
 * Applied at every depth: the tokenizer also runs inside quotes, list items and
 * footnotes, and a marker left there is not a schema node - `nodeFromJSON`
 * would throw. Walking every content array guarantees no unknown node stays.
 *
 * A blank line between the option line and the table still merges; it is gone
 * after normalizing (the writer puts the line right above the table).
 */
export function mergeTableOptions(doc: JSONContent): JSONContent {
  return mergeTableOptionsIn(doc);
}

function mergeTableOptionsIn(node: JSONContent): JSONContent {
  const children = node.content;
  if (!children || children.length === 0) return node;
  const out: JSONContent[] = [];
  let changed = false;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.type !== "tableOptions") {
      const merged = mergeTableOptionsIn(child);
      if (merged !== child) changed = true;
      out.push(merged);
      continue;
    }
    changed = true;
    const line = String(child.attrs?.line ?? "");
    const next = children[i + 1];
    if (next?.type === "table") {
      const { attrs } = parseTableOptions(line);
      const table = mergeTableOptionsIn(next);
      out.push({
        ...table,
        attrs: {
          ...(table.attrs ?? {}),
          widths: normalizeWidths(attrs.widths, columnCount(table)),
          fixRows: attrs.fixRows,
          fixCols: attrs.fixCols,
        },
      });
      i += 1;
    } else {
      out.push({ type: "opaqueBlock", attrs: { raw: line } });
    }
  }
  return changed ? { ...node, content: out } : node;
}

/**
 * The element carrying the three data attributes (the `div.trameria-table-scroll`
 * around the table). An attribute's `parseHTML` gets the matched `<table>`, so
 * it looks one level up; the class check keeps a plain pasted table's parent
 * from being read by accident.
 */
function tableOptionHost(element: {
  parentElement?: {
    classList?: { contains(name: string): boolean };
    getAttribute(name: string): string | null;
  } | null;
}): { getAttribute(name: string): string | null } | null {
  const parent = element.parentElement;
  if (!parent?.classList?.contains("trameria-table-scroll")) return null;
  return parent;
}

/** Reads `data-widths="3,14"` (null when any value is not a positive number). */
function widthsFromAttr(raw: string | null): number[] | null {
  if (!raw) return null;
  const nums = raw.split(",").map((v) => Number(v));
  if (nums.length === 0 || nums.some((n) => !Number.isFinite(n) || n <= 0))
    return null;
  return nums.map(roundColumnEm);
}

/** Reads `data-fix-rows` / `data-fix-cols` (0 unless 1 or 2). */
function fixFromAttr(raw: string | null): 0 | 1 | 2 {
  const n = Number(raw);
  return n === 1 || n === 2 ? n : 0;
}

// --- drags that start inside a cell -------------------------------------------

export const tableCellDragKey = new PluginKey("trameriaTableCellDrag");

/** The DOM shape needed here (the package has no DOM types). */
interface MaybeElement {
  nodeType?: number;
  parentElement?: MaybeElement | null;
  closest?: (selector: string) => unknown;
}

function targetInCell(target: unknown): boolean {
  const node = target as MaybeElement | null;
  if (!node) return false;
  const element = node.nodeType === 1 ? node : (node.parentElement ?? null);
  return !!element?.closest?.("td, th");
}

/**
 * A drag that starts inside a cell is a selection, not a text move.
 *
 * Dragging from text in one cell to the next to select cells moved the text
 * instead, and added an empty column to the table; only a drag from the empty
 * part of a cell selected cells. Cancelling `dragstart` stops the browser's
 * text drag and prosemirror-view's drag handling, so mousedown / mousemove go on
 * as a selection and prosemirror-tables makes a `CellSelection`. Moving text by
 * drag inside a table is rarely wanted. Block handle drags start outside
 * view.dom and are not affected.
 */
export function createTableCellDragPlugin(): Plugin {
  return new Plugin({
    key: tableCellDragKey,
    props: {
      handleDOMEvents: {
        // Only the drag's starting element counts. Judging by the selection
        // would also stop drags started outside the table while a selection
        // spans a paragraph and a cell.
        dragstart: (_view, event) => {
          if (!targetInCell(event.target)) return false;
          event.preventDefault();
          // Stop propagation too: prosemirror-tables listens for `dragstart`
          // on the root and removes its cell-selection `mousemove` when one
          // arrives, which falls back to plain text selection.
          event.stopPropagation();
          return true;
        },
      },
    },
  });
}

/**
 * {@link createTableCellDragPlugin} as its own extension. It cannot live in
 * `TrameriaTable.addProseMirrorPlugins`: the screen replaces the table with
 * `TrameriaTable.extend({ addNodeView })`, and `extend` copies the parent
 * config, so a keyed plugin would be added twice and the editor would not start.
 */
export const TableCellDrag = Extension.create({
  name: "tableCellDrag",
  addProseMirrorPlugins() {
    return [createTableCellDragPlugin()];
  },
});

/** The upstream separator-row test (same expression as the table's `start`). */
const TABLE_SEPARATOR_RE = /^[ \t|:]*-[ \t|:-]*$/;
const upstreamTableTokenizer = Table.config.markdownTokenizer;

/**
 * The upstream `start`, looking at the first two lines only. Upstream splits
 * the whole rest of the document to read two lines, which makes one parse cost
 * "paragraphs x document length".
 */
function tableStart(src: string): number {
  const first = src.indexOf("\n");
  if (first === -1) return -1;
  const second = src.indexOf("\n", first + 1);
  const sep = src.slice(first + 1, second === -1 ? src.length : second);
  if (!TABLE_SEPARATOR_RE.test(sep) || !sep.includes("|")) return -1;
  return src.slice(0, first).includes("|") ? 0 : -1;
}

export const TrameriaTable = Table.extend({
  markdownTokenizer: upstreamTableTokenizer && {
    ...upstreamTableTokenizer,
    start: tableStart,
  },
  addAttributes() {
    return {
      ...this.parent?.(),
      // `renderHTML: () => ({})` keeps TipTap from writing raw `widths="3,14"`
      // attributes. The table's own `renderHTML` below writes `data-widths` /
      // `data-fix-rows` / `data-fix-cols` on the outer div and `parseHTML`
      // reads them back: the clipboard goes through both, so without them
      // copying a table inside the editor would lose its widths.
      widths: {
        default: null,
        parseHTML: (element) =>
          widthsFromAttr(
            tableOptionHost(element)?.getAttribute("data-widths") ?? null,
          ),
        renderHTML: () => ({}),
      },
      fixRows: {
        default: 0,
        parseHTML: (element) =>
          fixFromAttr(
            tableOptionHost(element)?.getAttribute("data-fix-rows") ?? null,
          ),
        renderHTML: () => ({}),
      },
      fixCols: {
        default: 0,
        parseHTML: (element) =>
          fixFromAttr(
            tableOptionHost(element)?.getAttribute("data-fix-cols") ?? null,
          ),
        renderHTML: () => ({}),
      },
    };
  },
  // DOM for the static view and the clipboard (`div.trameria-table-scroll` >
  // `table[colgroup]`, the same shape the live node view draws). The block id
  // goes on the outer div, like other nodes with their own renderHTML.
  renderHTML({ node, HTMLAttributes }) {
    const widths = node.attrs.widths as number[] | null;
    const total = widths ? widths.reduce((a, b) => a + b, 0) : null;
    const scrollAttrs: Record<string, string> = {
      ...HTMLAttributes,
      class: "trameria-table-scroll",
    };
    // Carried only for the clipboard (the colgroup does the look), so not
    // written for the defaults.
    if (widths && widths.length > 0)
      scrollAttrs["data-widths"] = widths.map((w) => String(w)).join(",");
    if (node.attrs.fixRows)
      scrollAttrs["data-fix-rows"] = String(node.attrs.fixRows);
    if (node.attrs.fixCols)
      scrollAttrs["data-fix-cols"] = String(node.attrs.fixCols);
    const tableAttrs: Record<string, string> = {};
    if (total !== null)
      tableAttrs.style = `table-layout: fixed; width: ${total}em`;
    const colgroup = widths
      ? [
          "colgroup",
          {},
          ...widths.map((w) => ["col", { style: `width: ${w}em` }]),
        ]
      : null;
    return [
      "div",
      scrollAttrs,
      colgroup
        ? ["table", tableAttrs, colgroup, ["tbody", 0]]
        : ["table", tableAttrs, ["tbody", 0]],
    ];
  },
  renderMarkdown(node, h) {
    const table = renderTableToMarkdown(hidePipes(node), h).replaceAll(
      PIPE_PLACEHOLDER,
      "\\|",
    );
    const line = formatTableOptions({
      widths: (node.attrs?.widths as number[] | null) ?? null,
      fixRows: (node.attrs?.fixRows as 0 | 1 | 2) ?? 0,
      fixCols: (node.attrs?.fixCols as 0 | 1 | 2) ?? 0,
    });
    // The table starts with "\n", so the option line sits right above it.
    return line ? `${line}${table}` : table;
  },
});

export const TrameriaTableCell = TableCell.extend({ content: "paragraph+" });
export const TrameriaTableHeader = TableHeader.extend({
  content: "paragraph+",
});
